import { z } from 'zod';
import { PERMISSIONS, type Permission } from '@voll-intelligence/voll-authz';
import type { HttpService } from '@web/data/services';

const OrgEntrySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  role: z.enum(['owner', 'admin', 'member', 'guest']),
});

const UserRecordSchema = z.object({
  id: z.string(),
  cognitoSub: z.string(),
  email: z.string(),
  name: z.string(),
  isStaff: z.boolean(),
  displayName: z.string().optional(),
  phone: z.string().optional(),
  whatsapp: z.string().optional(),
});

const MeResponseSchema = UserRecordSchema.extend({
  orgs: z.array(OrgEntrySchema),
});

const KNOWN_PERMISSIONS: ReadonlySet<string> = new Set(PERMISSIONS);
const isPermission = (p: string): p is Permission => KNOWN_PERMISSIONS.has(p);

const MePermissionsResponseSchema = z.object({
  // A newer accounts-api may emit a permission this bundle does not know yet:
  // drop it and keep the known ones — never a ValidationError for the page.
  permissions: z.array(z.string()).transform((ps) => ps.filter(isPermission)),
});

const UpdateMeInputSchema = z
  .object({
    displayName: z.string().trim().min(1).max(80).nullish(),
    phone: z.string().trim().min(1).max(40).nullish(),
    whatsapp: z.string().trim().min(1).max(40).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export type Me = z.infer<typeof MeResponseSchema>;
export type MeUser = z.infer<typeof UserRecordSchema>;
export type OrgEntry = z.infer<typeof OrgEntrySchema>;
type MePermissions = z.infer<typeof MePermissionsResponseSchema>;
export type UpdateMeInput = z.infer<typeof UpdateMeInputSchema>;

interface MeRepository {
  getMe: () => Promise<Me>;
  getMyPermissions: (orgId: string) => Promise<MePermissions>;
  patchMe: (input: UpdateMeInput) => Promise<MeUser>;
}

/**
 * Wraps accounts-api `/me` and `/me/permissions`. The host of these
 * endpoints is the accounts-api (separate base URL from kanban-api).
 */
export function createMeRepository(accountsHttp: HttpService): MeRepository {
  async function getMe(): Promise<Me> {
    return accountsHttp.get('/me', { schema: MeResponseSchema });
  }

  async function getMyPermissions(orgId: string): Promise<MePermissions> {
    return accountsHttp.get('/me/permissions', {
      query: { orgId },
      schema: MePermissionsResponseSchema,
    });
  }

  async function patchMe(input: UpdateMeInput): Promise<MeUser> {
    return accountsHttp.patch('/me', UpdateMeInputSchema.parse(input), {
      schema: UserRecordSchema,
    });
  }

  return { getMe, getMyPermissions, patchMe };
}
